import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import { useTheme } from '../useTheme';
import { COMMAND_GROUPS, filterCommands, type Command } from './commands';

const LIST_ID = 'command-palette-list';

function optionDomId(command: Command): string {
  return `command-palette-option-${command.id}`;
}

interface CommandPaletteProps {
  /** Close the palette. The owner unmounts this component. */
  onDismiss: () => void;
}

/**
 * Ctrl K palette: one input, a filtered list of destinations and actions.
 *
 * Native `<dialog>`, like `Modal`, so Escape, focus containment and the backdrop
 * come from the platform — but without the modal's title bar and close button,
 * which are chrome a palette does not need.
 *
 * Focus stays on the input; the highlighted row is announced through
 * `aria-activedescendant` (the standard combobox pattern) rather than moving
 * focus row to row.
 */
export function CommandPalette({ onDismiss }: CommandPaletteProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { theme, setPreference } = useTheme();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const groups = useMemo(() => {
    // Only the theme switch that would change something is offered.
    const matches = filterCommands(query).filter(
      (command) => command.effect.kind !== 'set-theme' || command.effect.theme !== theme,
    );
    return COMMAND_GROUPS.map((group) => ({
      ...group,
      commands: matches.filter((command) => command.group === group.key),
    })).filter((group) => group.commands.length > 0);
  }, [query, theme]);

  // Flattened in display order, so arrow keys walk what the eye walks.
  const ordered = useMemo(() => groups.flatMap((group) => group.commands), [groups]);

  const activeIndex = ordered.length === 0 ? -1 : Math.min(highlight, ordered.length - 1);
  const activeCommand = activeIndex < 0 ? undefined : ordered[activeIndex];
  const activeId = activeCommand ? optionDomId(activeCommand) : undefined;

  // Runs before the `showModal` effect below, so it captures the real opener.
  // Unmounting a dialog skips the platform's own focus restore; do it by hand.
  useEffect(() => {
    const opener = document.activeElement;
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  // Keep the highlighted row in view when the arrow keys run past the fold.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  function runCommand(command: Command) {
    onDismiss();
    if (command.effect.kind === 'sign-out') {
      void signOut();
      return;
    }
    if (command.effect.kind === 'set-theme') {
      setPreference(command.effect.theme);
      return;
    }
    void navigate(command.effect.to);
  }

  function moveHighlight(delta: number) {
    if (ordered.length === 0) return;
    setHighlight((current) => {
      const from = Math.min(current, ordered.length - 1);
      return (from + delta + ordered.length) % ordered.length;
    });
  }

  function handleInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveHighlight(-1);
        break;
      case 'Home':
        event.preventDefault();
        setHighlight(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlight(Math.max(ordered.length - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (activeCommand) runCommand(activeCommand);
        break;
      default:
        break;
    }
  }

  return (
    // data-command-palette: see COMMAND_PALETTE_ROOT_ATTR — keeps Ctrl K live inside.
    <dialog
      className="palette"
      data-command-palette=""
      ref={dialogRef}
      aria-label="Command palette"
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
      onClick={(event) => {
        // Clicks on the backdrop land on the dialog element itself.
        if (event.target === dialogRef.current) onDismiss();
      }}
    >
      <div className="palette__search">
        <input
          className="palette__input"
          type="text"
          role="combobox"
          aria-label="Command or destination"
          aria-expanded="true"
          aria-controls={LIST_ID}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          autoComplete="off"
          autoFocus
          placeholder="Type a command or destination"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlight(0);
          }}
          onKeyDown={handleInputKeyDown}
        />
        <kbd className="kbd">Esc</kbd>
      </div>

      <div
        className="palette__list"
        id={LIST_ID}
        ref={listRef}
        role="listbox"
        aria-label="Commands"
      >
        {ordered.length === 0 ? (
          <p className="palette__empty">No matches</p>
        ) : (
          groups.map((group) => (
            <div
              className="palette__group"
              key={group.key}
              role="group"
              aria-labelledby={`command-palette-group-${group.key}`}
            >
              <p className="palette__group-label" id={`command-palette-group-${group.key}`}>
                {group.label}
              </p>
              {group.commands.map((command) => {
                const isActive = command.id === activeCommand?.id;
                return (
                  <div
                    className={
                      isActive ? 'palette__option palette__option--active' : 'palette__option'
                    }
                    key={command.id}
                    id={optionDomId(command)}
                    role="option"
                    aria-selected={isActive}
                    data-active={isActive ? 'true' : undefined}
                    onMouseMove={() => setHighlight(ordered.indexOf(command))}
                    onClick={() => runCommand(command)}
                  >
                    <span>{command.label}</span>
                    {isActive ? <kbd className="kbd">Enter</kbd> : null}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </dialog>
  );
}
